/**
 * The primitives every screen is built from.
 *
 * Kept in one file because there are only a dozen of them and they share the
 * same handful of decisions -- one focus ring, one elevation scale, one set of
 * disabled semantics. Splitting them across a dozen files would hide that.
 */

import {
  forwardRef,
  useEffect,
  useId,
  useRef,
  type ButtonHTMLAttributes,
  type InputHTMLAttributes,
  type ReactNode,
  type SelectHTMLAttributes,
  type TextareaHTMLAttributes,
} from 'react';
import { Loader2, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';

export function cx(...parts: Array<string | false | null | undefined>): string {
  return parts.filter(Boolean).join(' ');
}

// ---------------------------------------------------------------- Button

type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'danger';

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  loading?: boolean;
  icon?: ReactNode;
}

const BUTTON_VARIANTS: Record<ButtonVariant, string> = {
  primary: 'bg-brand text-on-brand hover:bg-brand-strong',
  secondary: 'border border-line bg-surface-raised text-ink hover:bg-surface-sunken',
  ghost: 'text-ink-muted hover:bg-surface-sunken hover:text-ink',
  danger: 'bg-danger text-white hover:brightness-110',
};

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  { variant = 'secondary', loading = false, icon, className, children, disabled, ...rest },
  ref,
) {
  return (
    <button
      ref={ref}
      type="button"
      // `aria-busy` rather than only a spinner: a screen reader gets no
      // spinner. Disabled while loading, so a double click cannot send twice.
      aria-busy={loading || undefined}
      disabled={disabled || loading}
      className={cx(
        'inline-flex min-h-[2.25rem] items-center justify-center gap-2 rounded-lg px-3',
        'text-sm font-medium transition-colors',
        'disabled:cursor-not-allowed disabled:opacity-50',
        BUTTON_VARIANTS[variant],
        className,
      )}
      {...rest}
    >
      {loading ? <Loader2 aria-hidden className="h-4 w-4 animate-spin" /> : icon}
      {children}
    </button>
  );
});

/**
 * A button whose whole content is an icon.
 *
 * `label` is not optional. An icon-only control with no accessible name is
 * invisible to a screen reader, and it is the single easiest a11y failure to
 * ship.
 */
export function IconButton({
  label,
  icon,
  ...rest
}: Omit<ButtonProps, 'children' | 'icon'> & { label: string; icon: ReactNode }) {
  return (
    <Button aria-label={label} title={label} className="w-9 px-0" {...rest}>
      {icon}
    </Button>
  );
}

// ----------------------------------------------------------------- Fields

interface FieldProps {
  label: string;
  hint?: string;
  error?: string;
  children: (id: string, describedBy: string | undefined) => ReactNode;
}

/**
 * Label above, hint and error below, wired together by id.
 *
 * A placeholder is not a label: it disappears the moment somebody types, and
 * an input the user has half-filled then has no name at all.
 */
export function Field({ label, hint, error, children }: FieldProps) {
  const id = useId();
  const hintId = hint ? `${id}-hint` : undefined;
  const errorId = error ? `${id}-error` : undefined;
  const describedBy = [errorId, hintId].filter(Boolean).join(' ') || undefined;

  return (
    <div className="space-y-1.5">
      <label htmlFor={id} className="block text-sm font-medium text-ink">
        {label}
      </label>
      {children(id, describedBy)}
      {error ? (
        <p id={errorId} role="alert" className="text-xs text-danger">
          {error}
        </p>
      ) : null}
      {hint ? (
        <p id={hintId} className="text-xs text-ink-subtle">
          {hint}
        </p>
      ) : null}
    </div>
  );
}

// Width is deliberately not in here. Tailwind emits `w-auto` before `w-full`,
// so a caller passing `w-auto` to override a base `w-full` silently loses --
// which is how the toolbar dropdowns ended up spanning the whole row. Inputs
// opt into full width below; selects are sized by whoever places them.
const CONTROL =
  'block rounded-lg border bg-surface-raised px-3 py-2 text-sm text-ink ' +
  'placeholder:text-ink-subtle disabled:cursor-not-allowed disabled:opacity-50';

export const TextInput = forwardRef<
  HTMLInputElement,
  InputHTMLAttributes<HTMLInputElement> & { invalid?: boolean }
>(function TextInput({ invalid, className, ...rest }, ref) {
  return (
    <input
      ref={ref}
      aria-invalid={invalid || undefined}
      className={cx(CONTROL, 'w-full', invalid ? 'border-danger' : 'border-line', className)}
      {...rest}
    />
  );
});

export const TextArea = forwardRef<
  HTMLTextAreaElement,
  TextareaHTMLAttributes<HTMLTextAreaElement> & { invalid?: boolean }
>(function TextArea({ invalid, className, ...rest }, ref) {
  return (
    <textarea
      ref={ref}
      aria-invalid={invalid || undefined}
      className={cx(
        CONTROL,
        'w-full min-h-[7rem] resize-y',
        invalid ? 'border-danger' : 'border-line',
        className,
      )}
      {...rest}
    />
  );
});

export const Select = forwardRef<
  HTMLSelectElement,
  SelectHTMLAttributes<HTMLSelectElement>
>(function Select({ className, children, ...rest }, ref) {
  return (
    <select
      ref={ref}
      className={cx(CONTROL, 'min-w-[9rem] border-line pr-8', className)}
      {...rest}
    >
      {children}
    </select>
  );
});

// ------------------------------------------------------------------ Badge

type Tone = 'neutral' | 'positive' | 'caution' | 'critical' | 'info';

const TONES: Record<Tone, string> = {
  neutral: 'bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-200',
  positive: 'bg-emerald-50 text-emerald-800 dark:bg-emerald-950 dark:text-emerald-200',
  caution: 'bg-amber-50 text-amber-900 dark:bg-amber-950 dark:text-amber-200',
  critical: 'bg-rose-50 text-rose-800 dark:bg-rose-950 dark:text-rose-200',
  info: 'bg-sky-50 text-sky-800 dark:bg-sky-950 dark:text-sky-200',
};

const DOTS: Record<Tone, string> = {
  neutral: 'bg-slate-400',
  positive: 'bg-emerald-500',
  caution: 'bg-amber-500',
  critical: 'bg-rose-500',
  info: 'bg-sky-500',
};

/**
 * Colour plus a word, never colour alone.
 *
 * The activity banding is the reason: red, amber and green are the one pair of
 * distinctions a colourblind reader is most likely to lose, and this is the
 * column the whole aftersales workflow reads.
 */
export function Badge({
  tone = 'neutral',
  dot = false,
  children,
  title,
}: {
  tone?: Tone;
  dot?: boolean;
  children: ReactNode;
  title?: string;
}) {
  return (
    <span
      title={title}
      className={cx(
        'inline-flex items-center gap-1.5 whitespace-nowrap rounded-full px-2 py-0.5',
        'text-2xs font-medium',
        TONES[tone],
      )}
    >
      {dot ? <span aria-hidden className={cx('h-1.5 w-1.5 rounded-full', DOTS[tone])} /> : null}
      {children}
    </span>
  );
}

// ------------------------------------------------------------------ Modal

export function Modal({
  open,
  onClose,
  title,
  description,
  children,
  footer,
}: {
  open: boolean;
  onClose: () => void;
  title: string;
  description?: string;
  children?: ReactNode;
  footer?: ReactNode;
}) {
  const { t } = useTranslation();
  const panel = useRef<HTMLDivElement>(null);
  const closeRef = useRef(onClose);
  closeRef.current = onClose;
  const titleId = useId();
  const descriptionId = useId();

  useEffect(() => {
    if (!open) return;
    const previous = document.activeElement as HTMLElement | null;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const controls = () => Array.from(panel.current?.querySelectorAll<HTMLElement>(
      'a[href], button, input:not([type="hidden"]), textarea, select, [tabindex]:not([tabindex="-1"])',
    ) ?? []).filter((element) => !element.matches(':disabled') && element.getClientRects().length > 0);
    const focusFirst = () => (controls()[0] ?? panel.current)?.focus();
    focusFirst();

    function onKeyDown(event: KeyboardEvent) {
      if (event.key === 'Escape') {
        event.preventDefault();
        closeRef.current();
      } else if (event.key === 'Tab') {
        const items = controls();
        const first = items[0];
        const last = items[items.length - 1];
        if (!first) { event.preventDefault(); panel.current?.focus(); return; }
        if (event.shiftKey && (document.activeElement === first || !panel.current?.contains(document.activeElement))) {
          event.preventDefault(); last.focus();
        } else if (!event.shiftKey && (document.activeElement === last || !panel.current?.contains(document.activeElement))) {
          event.preventDefault(); first.focus();
        }
      }
    }
    function onFocusIn(event: FocusEvent) {
      if (event.target instanceof Node && !panel.current?.contains(event.target)) focusFirst();
    }
    document.addEventListener('keydown', onKeyDown);
    document.addEventListener('focusin', onFocusIn);
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      document.removeEventListener('focusin', onFocusIn);
      document.body.style.overflow = previousOverflow;
      if (previous?.isConnected) previous.focus();
    };
  }, [open]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center p-0 sm:items-center sm:p-6">
      {/* Strong enough that the table behind stops competing for attention. */}
      <div
        className="absolute inset-0 bg-slate-900/50 backdrop-blur-[2px]"
        onClick={onClose}
        aria-hidden
      />
      <div
        ref={panel}
        tabIndex={-1}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        aria-describedby={description ? descriptionId : undefined}
        className={cx(
          'relative flex max-h-[92vh] w-full max-w-lg flex-col overflow-hidden',
          'rounded-t-card border border-line bg-surface-raised shadow-overlay sm:rounded-card',
        )}
      >
        <div className="flex items-start gap-3 border-b border-line px-5 py-4">
          <div className="min-w-0 flex-1">
            <h2 id={titleId} className="truncate text-base font-semibold text-ink">
              {title}
            </h2>
            {description ? (
              <p id={descriptionId} className="mt-1 text-xs text-ink-subtle">
                {description}
              </p>
            ) : null}
          </div>
          <IconButton
            variant="ghost"
            label={t('common.close')}
            icon={<X className="h-4 w-4" />}
            onClick={onClose}
          />
        </div>
        <div className="min-h-0 flex-1 overflow-y-auto px-5 py-4">{children}</div>
        {footer ? (
          <div className="flex justify-end gap-2 border-t border-line px-5 py-3">
            {footer}
          </div>
        ) : null}
      </div>
    </div>
  );
}

// ------------------------------------------------------------- Empty/error

export function EmptyState({
  icon,
  title,
  body,
  action,
}: {
  icon?: ReactNode;
  title: string;
  body: string;
  action?: ReactNode;
}) {
  return (
    <div className="flex flex-col items-center gap-2 px-6 py-16 text-center">
      {icon ? <div className="text-ink-subtle">{icon}</div> : null}
      <p className="text-sm font-medium text-ink">{title}</p>
      <p className="max-w-sm text-sm text-ink-subtle">{body}</p>
      {action ? <div className="mt-3">{action}</div> : null}
    </div>
  );
}

export function ErrorState({ message, onRetry }: { message: string; onRetry: () => void }) {
  const { t } = useTranslation();
  return (
    <div className="flex flex-col items-center gap-3 px-6 py-16 text-center">
      <p className="text-sm text-ink">{message}</p>
      <Button onClick={onRetry}>{t('common.retry')}</Button>
    </div>
  );
}

/**
 * Rows of shimmer, sized like the table that is coming.
 *
 * A spinner in the middle of an empty page moves the layout twice; a skeleton
 * the right height moves it once, when the data lands.
 */
export function TableSkeleton({ rows = 8, columns = 6 }: { rows?: number; columns?: number }) {
  return (
    <div className="divide-y divide-line" aria-hidden>
      {Array.from({ length: rows }).map((_, rowIndex) => (
        <div key={rowIndex} className="flex gap-4 px-3 py-3">
          {Array.from({ length: columns }).map((__, columnIndex) => (
            <div
              key={columnIndex}
              className="h-4 flex-1 animate-pulse rounded bg-surface-sunken"
            />
          ))}
        </div>
      ))}
    </div>
  );
}

// ------------------------------------------------------------------ Toggle

export function Toggle({
  checked,
  onChange,
  label,
  disabled,
}: {
  checked: boolean;
  onChange: (next: boolean) => void;
  label: string;
  disabled?: boolean;
}) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      aria-label={label}
      disabled={disabled}
      onClick={() => onChange(!checked)}
      className={cx(
        'relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors',
        'disabled:cursor-not-allowed disabled:opacity-50',
        checked ? 'bg-brand' : 'bg-slate-300 dark:bg-slate-600',
      )}
    >
      <span
        aria-hidden
        className={cx(
          'inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform',
          checked ? 'translate-x-6' : 'translate-x-1',
        )}
      />
    </button>
  );
}
